// useUnifiedModels — FONTE ÚNICA de modelos LLM pra TODA a UI do Apolo.
//
// Objetivo (Sprint unified-models): qualquer adapter/API que o user
// configurar aparece em TODOS os dropdowns (Chat, Code, Council, Schedules)
// com status "conectado" (bolinha verde) consistente.
//
// Agrega 3 camadas, deduplicado por model id:
//   1. MODEL_REGISTRY (built-in via plano Apolo) — source: 'registry'
//   2. adapter.available_models de cada adapter ativo — source: 'adapter'
//   3. adapterPresets fallback quando available_models vazio — source: 'preset'
//
// Status "connected":
//   • Cloud adapter → has_api_key === true
//   • Local (Ollama/LM Studio) → is_active !== false (decisão Pedro: ativo=verde,
//     sem ping real ao server — mais simples, sem latência)
//
// Multi-LLM zero-diff guard: NUNCA modifica modelRegistry.js — só IMPORTA.
//
// A agregação real vive na função PURA `buildUnifiedModels(adapters)` —
// exportada e testável sem React. O hook `useUnifiedModels` é só o wrapper
// que injeta os adapters (via useLLMAdapters) e memoiza.
//
// Shape de retorno:
//   {
//     groups: [{ provider, connected, models: [{ id, label, provider, source, connected, tier? }] }],
//     allModels: [{ id, label, provider, source, connected, tier? }],
//     byId: Record<id, { label, provider, source, connected, tier? }>,
//     labelForModel: (id) => string,
//     loading: bool,
//     adapters: rawAdapters[],
//   }

import { useCallback, useEffect, useMemo, useState } from 'react';
import { MODEL_REGISTRY } from '../lib/modelRegistry.js';
import { tierFromEntry } from '../lib/modelTiers.js';
import { getModelOptions } from '../lib/adapterPresets.js';
import { useLLMAdapters } from './useLLMAdapters.js';
import { apiFetch } from '../lib/api.js';
import { ADAPTER_NAME_TO_PROVIDER } from '../lib/models/connectedState.js';

const REGISTRY_PROVIDERS = new Set(MODEL_REGISTRY.map((m) => m.provider));

// O alias adapterName→provider (openai-api≠openai etc.) que fazia o dot não acender
// pros modelos do registry desses providers (#645) agora vive em lib/models/
// connectedState.js como FONTE ÚNICA (import acima) — aqui só consumimos.

// Heurística: adapter é local (Ollama/LM Studio) → connected basta is_active.
export function isLocalAdapter(adapter) {
  if (!adapter) return false;
  const slug = ((adapter.name || '') + ' ' + (adapter.display_name || '')).toLowerCase();
  if (slug.includes('ollama') || slug.includes('lmstudio') || slug.includes('lm studio')) return true;
  const url = (adapter.base_url || '').toLowerCase();
  return url.includes('11434') || url.includes('1234') || url.includes('localhost') || url.includes('127.0.0.1');
}

// Fonte de verdade do "connected" — usada por todos os dropdowns via este hook.
export function adapterConnected(adapter) {
  if (!adapter) return false;
  if (isLocalAdapter(adapter)) return adapter.is_active !== false;
  return !!adapter.has_api_key;
}

// Limpa prefixo emoji legado (🟢) de labels antigos.
function stripEmoji(s) {
  return typeof s === 'string' ? s.replace(/^🟢\s*/, '') : s;
}

/**
 * Núcleo PURO da agregação de modelos. Sem React — testável diretamente.
 *
 * @param {Array} adapters - lista crua de adapters (useLLMAdapters().adapters)
 * @returns {{ groups, allModels, byId, labelForModel }}
 */
export function buildUnifiedModels(adapters, extraModels = []) {
  const list = Array.isArray(adapters) ? adapters : [];

  // ── Connected por provider (built-ins do registry) ────────────────
  // Cada provider do registry tem adapter built-in correspondente.
  const providerConnected = {};
  for (const a of list) {
    if (!a.is_builtin) continue;
    const raw = a.provider || a.name;
    if (!raw) continue;
    const p = ADAPTER_NAME_TO_PROVIDER[raw] || raw;
    providerConnected[p] = providerConnected[p] || adapterConnected(a);
  }

  /** @type {Record<string, {id,label,provider,source,connected}>} */
  const byId = {};
  const pushModel = (m) => {
    if (!m.id || byId[m.id]) return; // dedup por id
    byId[m.id] = m;
  };

  // ── Camada 1: MODEL_REGISTRY (built-in plano Apolo) ───────────────
  // SEMPRE entra, independente de adapters. É o que garante que os modelos
  // built-in (DeepSeek, Anthropic, etc.) nunca somem do picker.
  for (const m of MODEL_REGISTRY) {
    pushModel({
      id: m.id,
      label: stripEmoji(m.label) || m.id,
      provider: m.provider,
      source: 'registry',
      connected: providerConnected[m.provider] === true,
      // design-uplift E4 — derived tier hint (Fast/Medium/High/Reasoning).
      // Renderer-only; the picker badges it. Optional: adapter/preset/discovered
      // models below have no registry entry → no tier → no badge.
      tier: tierFromEntry(m),
    });
  }

  // ── Camada 2+3: adapters configurados ─────────────────────────────
  for (const a of list) {
    const connected = adapterConnected(a);
    const adapterProvider = a.provider || a.name;
    // Built-ins que são providers do registry NÃO duplicam — o registry
    // já cobre os modelos canônicos deles.
    if (a.is_builtin && REGISTRY_PROVIDERS.has(adapterProvider)) continue;

    const displayName = a.display_name || a.name || 'Adapter';
    // available_models do D1 (fonte primária) → preset fallback.
    let modelList = Array.isArray(a.available_models) && a.available_models.length > 0
      ? a.available_models
      : (Array.isArray(a.models) && a.models.length > 0 ? a.models : null);
    if (!modelList || modelList.length === 0) {
      const preset = getModelOptions(a.name);
      modelList = preset.length > 0 ? preset : [];
    }

    for (const m of modelList) {
      if (!m || typeof m !== 'string') continue;
      pushModel({
        id: m,
        label: m,
        provider: displayName,
        source: a.is_builtin ? 'adapter' : 'preset',
        connected,
      });
    }
  }

  // ── Camada 4: modelos DESCOBERTOS (#6 — botão "Atualizar") ────────
  // Modelos novos que os providers lançaram, achados pelo /api/models/refresh.
  // pushModel dedupa por id → um id já no registry/adapter VENCE (não duplica);
  // só ids realmente novos entram. NÃO toca modelRegistry.js (guard).
  for (const m of (Array.isArray(extraModels) ? extraModels : [])) {
    if (!m || !m.id) continue;
    pushModel({
      id: m.id,
      label: m.label || m.id,
      provider: m.provider || 'discovered',
      source: 'discovered',
      connected: providerConnected[m.provider] === true,
    });
  }

  // ── Monta allModels + groups ──────────────────────────────────────
  const allModels = Object.values(byId);

  const groupMap = new Map();
  for (const m of allModels) {
    if (!groupMap.has(m.provider)) groupMap.set(m.provider, []);
    groupMap.get(m.provider).push(m);
  }

  // Ordem: registry providers primeiro (ordem canônica), depois resto
  // alfabético com conectados na frente.
  const registryOrder = [
    'anthropic', 'openai', 'google', 'deepseek', 'openrouter', 'ollama',
    'xai', 'mistral', 'groq', 'kimi', 'qwen', 'zai',
  ];
  const groups = [];
  for (const p of registryOrder) {
    if (groupMap.has(p)) {
      groups.push(buildGroup(p, groupMap.get(p)));
      groupMap.delete(p);
    }
  }
  const rest = Array.from(groupMap.keys()).sort((a, b) => {
    const aHas = groupMap.get(a).some((m) => m.connected);
    const bHas = groupMap.get(b).some((m) => m.connected);
    if (aHas !== bHas) return aHas ? -1 : 1;
    return a.localeCompare(b);
  });
  for (const p of rest) groups.push(buildGroup(p, groupMap.get(p)));

  function labelForModel(modelId) {
    if (!modelId) return null;
    return byId[modelId]?.label || modelId;
  }

  return { groups, allModels, byId, labelForModel };
}

/**
 * Hook unificado de modelos. Único ponto de agregação do Apolo.
 *
 * @param {object} [opts]
 * @param {string} [opts.kind='text'] - text|image|video|audio
 * @returns {{ groups, allModels, byId, labelForModel, loading, adapters }}
 */
// #6 — the user's discovered/extra models (GET /api/models/extra) + a refresh that
// queries every configured provider's /models for newly-launched models.
export function useExtraModels() {
  const [models, setModels] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    const res = await apiFetch('/api/models/extra');
    if (res.ok) setModels(Array.isArray(res.data?.models) ? res.data.models : []);
  }, []);

  useEffect(() => {
    load();
    // re-read when the "Atualizar LLMs" button (IntegrationsView) discovers models.
    const onChanged = () => load();
    window.addEventListener('apolo:models-changed', onChanged);
    return () => window.removeEventListener('apolo:models-changed', onChanged);
  }, [load]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await apiFetch('/api/models/refresh', { method: 'POST' });
      await load();
      return res.ok ? res.data : { added: 0, error: res.error };
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  return { models, refresh, refreshing };
}

export function useUnifiedModels({ kind = 'text' } = {}) {
  const { adapters, loading } = useLLMAdapters({ kind });
  const { models: extraModels, refresh: refreshModels, refreshing } = useExtraModels();

  return useMemo(() => {
    const built = buildUnifiedModels(adapters || [], extraModels);
    return { ...built, loading, adapters: adapters || [], refreshModels, refreshing };
  }, [adapters, loading, extraModels, refreshModels, refreshing]);
}

// Helper: monta { provider, connected, models } com models ordenados
// (conectados primeiro, alfabético).
function buildGroup(provider, models) {
  const sorted = [...models].sort((a, b) => {
    if (a.connected !== b.connected) return a.connected ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
  return {
    provider,
    connected: sorted.some((m) => m.connected),
    models: sorted,
  };
}
